import { useEffect, useState } from 'react';
import type { TurnClock } from './turn-status';

/** Tracks when the in-flight turn started and when it last changed.
 *
 *  `activity` is any value whose identity changes when the stream delivers an
 *  event. AI SDK snapshots the streaming message with `structuredClone` on
 *  every chunk, so the last message object works: a new reference means the
 *  server sent something. Returns null while no turn is in flight. */
export function useTurnClock(
  isStreaming: boolean,
  activity: unknown
): TurnClock | null {
  const [clock, setClock] = useState<TurnClock | null>(null);

  useEffect(() => {
    if (!isStreaming) {
      setClock(null);
      return;
    }
    const now = Date.now();
    setClock((prev) =>
      prev
        ? { ...prev, lastActivityAt: now }
        : { startedAt: now, lastActivityAt: now }
    );
  }, [isStreaming, activity]);

  return clock;
}

const TICK_MS = 1_000;

/** Current time, re-read once a second. */
export function useNow(): number {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(id);
  }, []);

  return now;
}
